import { useUser } from "@shared/utils/hooks/misc";
import { dateToMoment, formatDate } from "@shared/utils/date";
import moment from "moment";
import { sharedLib } from "@shared/services/shared.service";
import { DisplayResultMessage, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useCancelSubscriptionModalProps {
  closeModal: () => void;
  startSurvey?: () => void;
  displayResultMessage: DisplayResultMessage;
}

const useCancelSubscriptionModal = ({
  closeModal,
  startSurvey,
  displayResultMessage,
}: useCancelSubscriptionModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const expirationMoment = dateToMoment(user?.expiration_timestamp, true);
  const subscriptionDaysLeft = expirationMoment.diff(moment(), "days");

  const [cancelSubscription, { isLoading: isLoadingCancelSubscription }] =
    sharedLib.sharedApi.settings.useCancelSubscriptionMutation();

  const onCancelSubscriptionClick = () => {
    displayResultMessage(
      cancelSubscription().unwrap(),
      t("cancel_subscription_modal.success_message"),
      t("cancel_subscription_modal.error_message"),
      undefined
    ).then(() => {
      closeModal();
      if (subscriptionDaysLeft <= 15) {
        startSurvey?.();
      }
    });
  };

  const getModalContent = () => {
    const expirationMoment = dateToMoment(
      user?.original_expiration_timestamp,
      true
    );
    const isImmediateExpiration = expirationMoment.isBefore(moment());
    let texts: string[] = [];
    if (isImmediateExpiration) {
      texts = texts.concat([
        t("cancel_subscription_modal.immediate_end_message"),
      ]);
    } else {
      texts = texts.concat([
        t("cancel_subscription_modal.scheduled_end_message"),
        formatDate(user?.original_expiration_timestamp, false, true),
        t("cancel_subscription_modal.after_end_message"),
      ]);
    }
    texts.push(t("cancel_subscription_modal.confirm_message"));
    return texts;
  };

  return {
    isLoadingCancelSubscription,
    onCancelSubscriptionClick,
    modalContent: getModalContent(),
  };
};

export default useCancelSubscriptionModal;
